import React, { useState, useEffect } from 'react';
import { getChatList, deleteChat, ChatSession, getDefaultProb, setDefaultProb, getChatDisplayMessages, getModelName, setModelName, getModelList } from '../services/tauri-api';
import { extractLastUserMessageSync } from '../utils/extractLastUserMessage';
import { getSessionChatIds, removeSessionChatId } from '../utils/sessionStorage';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  globalBusy: boolean;
  onChatDeleted?: (deletedChatId: string) => void;
  initialTab?: TabType;
}

type TabType = 'general' | 'chats' | 'about';

const FEEDBACK_FORM_URL = 'https://forms.gle/Jnohrc4YCk27UvWp9';

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose, globalBusy, onChatDeleted, initialTab }) => {
  const [activeTab, setActiveTab] = useState<TabType>('general');
  const [chats, setChats] = useState<ChatSession[]>([]);
  const [chatPreviews, setChatPreviews] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [deletingChatId, setDeletingChatId] = useState<string | null>(null);
  const [confirmDeleteChatId, setConfirmDeleteChatId] = useState<string | null>(null);
  const [focusOnSelect, setFocusOnSelect] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.localStorage) return true;
    const v = window.localStorage.getItem('focusOnSelect');
    return v === null ? true : v === '1';
  });
  const [defaultProb, setDefaultProbState] = useState<number>(1.0);
  const [modelName, setModelNameState] = useState<string | null>(null);
  const [modelList, setModelList] = useState<string[]>([]);

  // initialTab が指定された場合、モーダルを開いたタイミングでそのタブを表示
  useEffect(() => {
    if (isOpen && initialTab) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  // チャット一覧を読み込み
  const loadChats = async () => {
    setIsLoading(true);
    try {
      const response = await getChatList();
      if (response.success && response.data) {
        // セッションストレージに保存されたチャットIDのみをフィルタリング
        const sessionChatIds = getSessionChatIds();
        const filteredChats = response.data.filter(chat => 
          sessionChatIds.includes(chat.chat_id)
        );
        
        // created_at でソート（新しい順）
        const sortedChats = filteredChats.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        setChats(sortedChats);
        
        // 各チャットのプレビューを取得
        const previews: Record<string, string> = {};
        for (const chat of sortedChats) {
          try {
            const displayResponse = await getChatDisplayMessages(chat.chat_id);
            if (displayResponse.success && displayResponse.data && displayResponse.data.length > 0) {
              const firstMessage = displayResponse.data[0];
              // チャットのmodel属性に基づいてパース
              // まず、チャットのmodel属性に基づいてタグ設定を取得
              if (chat.model) {
                const { getTagsConfigForModel } = await import('../utils/extractLastUserMessage');
                await getTagsConfigForModel(chat.model);
              }
              // ユーザーメッセージの場合、最後の <|start_header_id|>user<|end_header_id|> と <|eot_id|> の間の内容を抽出
              const content = firstMessage.role === 'human'
                ? extractLastUserMessageSync(firstMessage.content, chat.model)
                : firstMessage.content;
              const preview = content && content.length > 0
                ? (content.length > 50 
                    ? content.substring(0, 50) + '...'
                    : content)
                : 'メッセージなし';
              previews[chat.chat_id] = preview;
            } else {
              previews[chat.chat_id] = 'メッセージなし';
            }
          } catch (error) {
            console.error(`チャット ${chat.chat_id} のプレビュー取得に失敗:`, error);
            previews[chat.chat_id] = 'メッセージなし';
          }
        }
        setChatPreviews(previews);
      }
    } catch (error) {
      console.error('チャット一覧の読み込みに失敗:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // チャット削除の確認
  const handleDeleteChat = (chatId: string) => {
    console.log('[SettingsModal] handleDeleteChat 呼び出し:', { chatId, globalBusy, deletingChatId });
    
    if (globalBusy || deletingChatId) {
      console.log('[SettingsModal] 削除がブロックされています:', { globalBusy, deletingChatId });
      return;
    }
    
    console.log('[SettingsModal] 確認ダイアログを表示します');
    setConfirmDeleteChatId(chatId);
  };

  // チャット削除の実行
  const executeDeleteChat = async (chatId: string) => {
    console.log('[SettingsModal] 削除を実行します:', chatId);
    setConfirmDeleteChatId(null);
    setDeletingChatId(chatId);
    try {
      const response = await deleteChat(chatId);
      console.log('[SettingsModal] 削除レスポンス:', response);
      if (response.success) {
        console.log('[SettingsModal] 削除成功、チャット一覧を更新します');
        // セッションストレージからチャットIDを削除
        removeSessionChatId(chatId);
        setChats(prev => prev.filter(chat => chat.chat_id !== chatId));
        // サイドバーのチャット一覧をリロード
        if (onChatDeleted) {
          onChatDeleted(chatId);
        }
      } else {
        console.error('[SettingsModal] チャット削除失敗:', response.error);
        alert(`チャット削除に失敗しました: ${response.error || '不明なエラー'}`);
      }
    } catch (error) {
      console.error('[SettingsModal] チャット削除エラー:', error);
      alert(`チャット削除エラー: ${error instanceof Error ? error.message : '不明なエラー'}`);
    } finally {
      setDeletingChatId(null);
    }
  };

  // チャットのプレビューを取得（プレビュー用）
  const getChatPreview = (chat: ChatSession): string => {
    if (chat.messages.length === 0) return 'メッセージなし';
    return chatPreviews[chat.chat_id] || '読み込み中...';
  };

  // モーダルが開かれた時にチャット一覧を読み込み
  useEffect(() => {
    if (isOpen && activeTab === 'chats') {
      loadChats();
    }
  }, [isOpen, activeTab]);

  // 設定タブ表示時に localStorage から最新値を同期
  useEffect(() => {
    if (isOpen && activeTab === 'general') {
      try {
        const v = window.localStorage?.getItem('focusOnSelect');
        setFocusOnSelect(v === null ? true : v === '1');
      } catch (e) {
        // noop
      }
      (async () => {
        const res = await getDefaultProb();
        if (res.success && typeof res.data === 'number') {
          setDefaultProbState(res.data);
        }
        // モデル名を取得
        const modelRes = await getModelName();
        if (modelRes.success) {
          setModelNameState(modelRes.data ?? null);
        }
        // モデル一覧を取得
        const listRes = await getModelList();
        console.log('[SettingsModal] モデル一覧取得結果:', listRes);
        if (listRes.success && listRes.data) {
          console.log('[SettingsModal] モデル一覧:', listRes.data);
          setModelList(listRes.data);
        } else {
          console.error('[SettingsModal] モデル一覧取得失敗:', listRes.error);
        }
      })();
    }
  }, [isOpen, activeTab]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[80vh] flex flex-col">
        {/* ヘッダー */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">設定</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            aria-label="設定を閉じる"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* タブナビゲーション */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('general')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'general'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            一般設定
          </button>
          <button
            onClick={() => setActiveTab('chats')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'chats'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            チャット管理
          </button>
          <button
            onClick={() => setActiveTab('about')}
            className={`ml-auto px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'about'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            アプリ情報
          </button>
        </div>

        {/* タブコンテンツ */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'general' && (
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">一般設定</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">テーマ</h4>
                    <p className="text-sm text-gray-500">アプリケーションのテーマ</p>
                  </div>
                  <div className="px-3 py-2 bg-gray-100 text-gray-600 rounded-md text-sm">
                    ライト
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">言語</h4>
                    <p className="text-sm text-gray-500">表示言語</p>
                  </div>
                  <div className="px-3 py-2 bg-gray-100 text-gray-600 rounded-md text-sm">
                    日本語
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">ノード選択時にフォーカス</h4>
                    <p className="text-sm text-gray-500">ノードをクリックした際に自動でフォーカス/ズームします</p>
                  </div>
                  <button
                    onClick={() => {
                      const next = !focusOnSelect;
                      setFocusOnSelect(next);
                      try {
                        window.localStorage?.setItem('focusOnSelect', next ? '1' : '0');
                        // ストレージイベントで他コンポーネントへ通知
                        window.dispatchEvent(new StorageEvent('storage', { key: 'focusOnSelect', newValue: next ? '1' : '0' }));
                      } catch (e) { /* noop */ }
                    }}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${focusOnSelect ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                    title={focusOnSelect ? '有効' : '無効'}
                  >
                    {focusOnSelect ? '有効' : '無効'}
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">追加ノードのデフォルト確率</h4>
                    <p className="text-sm text-gray-500">0% または 100%</p> 
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={async () => {
                        setDefaultProbState(0.0);
                        await setDefaultProb(0.0);
                      }}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${defaultProb === 0 ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                    >
                      0%
                    </button>
                    <button
                      onClick={async () => {
                        setDefaultProbState(1.0);
                        await setDefaultProb(1.0);
                      }}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${defaultProb === 1 ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                    >
                      100%
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">AIモデル</h4>
                    <p className="text-sm text-gray-500">使用するAIモデルを選択</p>
                  </div>
                  <select
                    value={modelName || 'default'}
                    onChange={async (e) => {
                      const selectedModel = e.target.value === 'default' ? null : e.target.value;
                      setModelNameState(selectedModel);
                      await setModelName(selectedModel);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {modelList.map((model) => (
                      <option key={model} value={model}>
                        {model === 'default' ? 'デフォルト' : model}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'chats' && (
            <div className="p-6 h-full flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">チャット管理</h3>
                <button
                  onClick={loadChats}
                  disabled={isLoading}
                  className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
                >
                  {isLoading ? '読み込み中...' : '更新'}
                </button>
              </div>

              {isLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto">
                  {chats.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      チャットがありません
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {chats.map((chat) => (
                        <div
                          key={chat.chat_id}
                          className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {getChatPreview(chat)}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {chat.messages.length} メッセージ • {new Date(chat.created_at).toLocaleString('ja-JP')}
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteChat(chat.chat_id)}
                            disabled={globalBusy || deletingChatId === chat.chat_id}
                            className={`ml-4 px-3 py-1.5 text-sm rounded-md transition-colors ${
                              globalBusy || deletingChatId === chat.chat_id
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                            }`}
                          >
                            {deletingChatId === chat.chat_id ? (
                              <div className="flex items-center">
                                <div className="animate-spin rounded-full h-3 w-3 border-b border-red-600 mr-1"></div>
                                削除中...
                              </div>
                            ) : (
                              '削除'
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'about' && (
            <div className="p-6 space-y-6 overflow-y-auto">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">LLMView Open Beta</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  LLMView は、「AIの思考が『のぞける』『さわれる』教育アプリ」です。
                  現在は <span className="font-semibold text-gray-800">オープンベータ版</span> として提供しており、
                  仕様や挙動は今後のフィードバックに応じて変更される可能性があります。
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                  <h4 className="text-sm font-semibold text-gray-800 mb-1">コンセプト</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    「LLMの中で何が起きているか」を、
                    プロンプトと生成結果のツリー構造として直感的に追いかけられることを目指しています。
                  </p>
                </div>
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                  <h4 className="text-sm font-semibold text-gray-800 mb-1">開発</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    個人開発プロジェクトとして運営されています。
                    不具合報告や機能要望などがあれば、ぜひフィードバックをお寄せください。
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-gray-100 bg-white p-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">ご利用にあたって</h4>
                <ul className="text-xs text-gray-600 list-disc list-inside space-y-1">
                  <li>ベータ版のため、予告なく仕様変更・停止する場合があります。</li>
                  <li>重要な情報の保管用途には使用せず、検証・実験目的でのご利用を想定しています。</li>
                  <li>生成結果の品質や正確性については保証されません。</li>
                </ul>
              </div>

              <div className="rounded-xl border border-blue-50 bg-blue-50/60 p-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">フィードバック</h4>
                <p className="text-xs text-gray-600 leading-relaxed mb-3">
                  使ってみての気付きや「こうなったらいいな」というアイデアがあれば、ぜひフィードバックをお送りください。
                  今後の改善の参考にさせていただきます。
                </p>
                <button
                  type="button"
                  onClick={() => {
                    if (typeof window !== 'undefined') {
                      window.open(FEEDBACK_FORM_URL, '_blank', 'noopener,noreferrer');
                    }
                  }}
                  className="inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors"
                >
                  フィードバックフォームを開く
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 削除確認ダイアログ */}
      {confirmDeleteChatId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[80] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">チャット削除の確認</h3>
              <p className="text-sm text-gray-700 mb-6">
                このチャットと関連する全てのセッションを削除しますか？この操作は取り消せません。
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setConfirmDeleteChatId(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  キャンセル
                </button>
                <button
                  onClick={() => executeDeleteChat(confirmDeleteChatId)}
                  disabled={globalBusy || deletingChatId === confirmDeleteChatId}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  削除
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsModal;
